import React from 'react';
import { ConditionalWrap } from '@/components/common/Utils';
import { findChildByType } from '@/components/ui/ui.utils';
import Tooltip from './Tooltip';

interface TCITooltipProps {
  children: ReactChildren;
  contentClassName?: string;
  delayDuration?: number;
  disabled?: boolean;
  disablePortal?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  showArrow?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  theme?: 'white' | null;
}

const TCITooltipComponent = React.forwardRef<
React.ElementRef<typeof Tooltip.Trigger>,
TCITooltipProps
>(({
  children,
  contentClassName,
  delayDuration = 0,
  disabled = false,
  disablePortal = false,
  onOpenChange,
  open,
  showArrow = true,
  side = 'top',
  sideOffset = 4,
  theme = 'white',
}, ref) => {
  const contentChild = findChildByType(children, Tooltip.Content);
  const triggerChild = findChildByType(children, Tooltip.Trigger);

  if (!contentChild || !triggerChild) {
    throw new Error('TCITooltip must have both Tooltip.Content and Tooltip.Trigger children');
  }

  return (
    <Tooltip.Provider delayDuration={delayDuration}>
      <Tooltip open={disabled ? false : open} onOpenChange={onOpenChange}>
        <Tooltip.Trigger ref={ref} {...triggerChild.props}>
          {triggerChild.props.children}
        </Tooltip.Trigger>
        <ConditionalWrap
          condition={!disablePortal}
          wrap={(c: React.ReactNode) => <Tooltip.Portal>{c}</Tooltip.Portal>}
        >
          <Tooltip.Content
            {...(contentChild as React.ReactElement).props}
            className={contentClassName ?? (contentChild as React.ReactElement).props.className}
            disabled={disabled}
            showArrow={showArrow}
            side={side}
            sideOffset={sideOffset}
            theme={theme}
          >
            {(contentChild as React.ReactElement).props.children}
          </Tooltip.Content>
        </ConditionalWrap>
      </Tooltip>
    </Tooltip.Provider>
  );
});

TCITooltipComponent.displayName = 'TCITooltip';

const TCITooltipContent = Tooltip.Content;
TCITooltipContent.displayName = 'TCITooltip.Content';

const TCITooltipTrigger = Tooltip.Trigger;
TCITooltipTrigger.displayName = 'TCITooltip.Trigger';

const TCITooltipPortal = Tooltip.Portal;
TCITooltipPortal.displayName = 'TCITooltip.Portal';

const TCITooltip = Object.assign(TCITooltipComponent, {
  Content: TCITooltipContent,
  Portal: TCITooltipPortal,
  Trigger: TCITooltipTrigger,
});

export default TCITooltip;
